"use client";
import React, { useEffect, useState, useRef } from "react";
import { Navbar } from "@nextui-org/react";
import { 
  FaSearch, 
  FaBars, 
  FaTimes, 
  FaUser, 
  FaSignOutAlt, 
  FaWallet, 
  FaEnvelope, 
  FaBell, 
  FaPlus, 
  FaCog 
} from 'react-icons/fa';
import io from 'socket.io-client';

import NavLink from "./NavLink";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import axiosAuth from "@/app/utils/axiosAuth";


export default function TopNavbar() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string>("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [active, setActive] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [hasMounted, setHasMounted] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>({});
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef<any>(null);
   
  const pathname = usePathname();
  const router = useRouter();

  // Fetch unread notification count
  const fetchUnreadCount = async (token: string, userId: string) => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/notifications/unread-count`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set current user from localStorage
  useEffect(() => {
    const currentUserString = localStorage.getItem("currentUser");
    setCurrentUser(currentUserString ? JSON.parse(currentUserString) : {});
  }, []);
  
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Check login status and fetch unread count
  useEffect(() => {
    const checkLoginStatus = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsLoggedIn(false);
        setIsVerified(false);
        setProfilePicture("/default-profile.png");
        setCurrentUser({});
        localStorage.removeItem("currentUser");
        return;
      }
      try {
        const res = await axiosAuth.get(
          `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/auth/me`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setIsLoggedIn(true);
        setIsVerified(res.data.isVerified);
        setProfilePicture(res.data.profilePicture || "/default-profile.png");
        setCurrentUser(res.data);
        localStorage.setItem("currentUser", JSON.stringify(res.data));
        
        // Fetch unread notification count
        fetchUnreadCount(token, res.data._id);
      } catch (err) {
        setIsLoggedIn(false);
        setIsVerified(false);
        setProfilePicture("/default-profile.png");
        setCurrentUser({});
        localStorage.removeItem("currentUser");
      }
    };

    checkLoginStatus();
  }, []);

  // Socket.IO for real-time notification updates
  useEffect(() => {
    if (!isLoggedIn || !currentUser._id) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Connect to Socket.IO
    socketRef.current = io(process.env.NEXT_PUBLIC_API_BASE_URL, {
      auth: {
        token,
      },
    });

    // Listen for new notifications
    socketRef.current.on('notification:new', () => {
      setUnreadCount(prev => prev + 1);
    });

    // Listen for marked read
    socketRef.current.on('notification:marked-read', () => {
      setUnreadCount(prev => Math.max(0, prev - 1));
    });

    // Listen for all marked read
    socketRef.current.on('notification:all-marked-read', () => {
      setUnreadCount(0);
    });

    // Listen for deleted notification
    socketRef.current.on('notification:deleted', () => {
      setUnreadCount(prev => Math.max(0, prev - 1));
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [isLoggedIn, currentUser._id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await axiosAuth.post(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/auth/logout`, 
        {}, 
        {}
      );
      localStorage.removeItem("token");
      localStorage.removeItem("currentUser");
      setIsLoggedIn(false);
      setIsVerified(false);
      setProfilePicture("/default-profile.png");
      setUnreadCount(0);
      router.push("/auth/login");
      console.log("User logged out");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navItems = [
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/community', label: 'Community' },
    { href: '/tasks', label: 'Find Tasks' },
  ];

return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-white shadow-md border-b border-gray-100' 
        : 'bg-white border-b border-gray-100'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link href="/" className="text-2xl font-bold cursor-pointer bg-clip-text text-transparent font-custom text-logo bg-purple-700">
            hitmeup!
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  pathname === item.href
                    ? 'text-purple-600 bg-purple-50'
                    : 'text-gray-700 hover:text-purple-600'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right side controls */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <button className="p-2 text-gray-500 hover:text-purple-600 transition-colors">
              <FaSearch className="w-4 h-4" />
            </button>

            {isLoggedIn ? (
              <>
                {/* Icons */}
                <div className="hidden md:flex items-center space-x-3">
                  <Link href="/wallet" className="p-2 text-gray-600 hover:text-purple-600 transition-colors">
                    <FaWallet className="w-4 h-4" />
                  </Link>
                  <Link href="/messages" className="p-2 text-gray-600 hover:text-purple-600 transition-colors">
                    <FaEnvelope className="w-4 h-4" />
                  </Link>
                  <Link href="/notifications" className="relative p-2 text-gray-600 hover:text-purple-600 transition-colors">
                    <FaBell className="w-4 h-4" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>
                </div>

                {/* User dropdown */}
                <div className="relative profile-dropdown">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="relative flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-700 focus:outline-none"
                  >
                    <FaUser className="w-4 h-4" />
                    {/* AUN Student Badge */}
                    {currentUser?.isAunStudent && currentUser?.hasCompleteProfile && (
                      <span 
                        className="absolute -top-1 -right-1 text-xs bg-white rounded-full w-4 h-4 flex items-center justify-center shadow-md"
                        title="AUN Student - 21% Commission Rate"
                      >
                        🎓
                      </span>
                    )}
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      {/* AUN Student Status */}
                      {currentUser?.isAunStudent && (
                        <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
                          <div className="flex items-center text-sm">
                            <span className="text-lg mr-2">🎓</span>
                            <div className="flex-1">
                              <div className="font-semibold text-purple-700">AUN Student</div>
                              {currentUser?.hasCompleteProfile ? (
                                <div className="text-xs text-green-600">✓ 21% Commission Rate Active</div>
                              ) : (
                                <div className="text-xs text-orange-600">⚠ Complete profile to activate benefits</div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      <Link
                        href="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <FaUser className="mr-3 text-purple-600" />
                        My Profile
                      </Link>
                      <Link
                        href="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <FaCog className="mr-3 text-purple-600" />
                        Settings
                      </Link>
                      <Link
                        href="/new-listing"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <FaPlus className="mr-3 text-purple-600" />
                        Create Listing
                      </Link>
                      <div className="border-t border-gray-200 my-1"></div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600"
                      >
                        <FaSignOutAlt className="mr-3 text-purple-600" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Auth buttons */
              <div className="hidden md:flex items-center space-x-3">
                <Link
                  href="/auth/login"
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors"
                >
                  Log In
                </Link>
                <Link
                  href="/auth/register"
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-md text-sm font-medium hover:shadow-md transition-all"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-500 hover:text-purple-600 focus:outline-none mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <FaTimes className="w-5 h-5" />
              ) : (
                <FaBars className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden mobile-menu bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  pathname === item.href
                    ? 'text-purple-600 bg-purple-50'
                    : 'text-gray-700 hover:text-purple-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            
            {isLoggedIn ? (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  href="/wallet"
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FaWallet className="mr-3 text-purple-600" />
                  Wallet
                </Link>
                <Link
                  href="/messages"
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FaEnvelope className="mr-3 text-purple-600" />
                  Messages
                </Link>
                <Link
                  href="/notifications"
                  className="relative flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FaBell className="mr-3 text-purple-600" />
                  Notifications
                  {unreadCount > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
                <Link
                  href="/new-listing"
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FaPlus className="mr-3 text-purple-600" />
                  Create Listing
                </Link>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  href="/profile"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Profile
                </Link>
                <Link
                  href="/settings"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Settings
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  href="/auth/login"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Log In
                </Link>
                <Link
                  href="/auth/register"
                  className="block px-3 py-2 rounded-md text-base font-medium bg-gradient-to-r from-purple-600 to-blue-500 text-white hover:shadow-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
} 